import React, {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import "../assets/css/Certificate.css";
import Logo from "../assets/image/logo/logo.png";
import { fetchUserEnrolledCohorts } from "../redux/slices/userEnrolledCohortSlice.js";
import { useDispatch, useSelector } from "react-redux";
import { usePDF } from "react-to-pdf";
import CertificateSVG from "./CertificateSVG";

// Optional: add scanned signature images (transparent PNGs) and import them here.
// import DirectorSignature from "../assets/image/logo/director-signature.png";
// import InstructorSignature from "../assets/image/logo/instructor-signature.png";
import DirectorSignature from "../assets/image/logo/director-signature.png";
import InstructorSignature from "../assets/image/logo/instructor-signature.png";

// A4 landscape at 96dpi. Must match .certificate in Certificate.css
const CERT_W = 1123;
const CERT_H = 794;

const DEFAULT_DESCRIPTION =
  "This certification recognises the recipient’s demonstrated proficiency in essential data analysis competencies, including data acquisition, cleaning and transformation, statistical analysis, and data storytelling through visualisation.";

// The "default" certificate (same as the template, with placeholders)
const DEFAULT_CERTIFICATE = {
  name: "NAME OF STUDENT",
  program: "Professional ADD COURSE TITLE Program",
  date: "DATE APPROVED",
  description: DEFAULT_DESCRIPTION,
  directorName: "OLUWAFUNMIKE ADEYEMI",
  directorTitle: "PROGRAM DIRECTOR",
  instructorName: "BENEDICT EMOEKABU",
  instructorTitle: "LEAD INSTRUCTOR",
  certificateId: "RC2025DA6008",
};

// Generate certificate ID based on cohort name
const generateCertificateId = (cohortName, enrollmentItem) => {
  const words = cohortName.split(" ").filter((word) => word.length > 0);
  const prefix = "RC";
  const year = new Date().getFullYear();
  const firstLetter = words[0] ? words[0].charAt(0).toUpperCase() : "C";
  const secondLetter = words[1] ? words[1].charAt(0).toUpperCase() : "O";
  return `${prefix}${year}${firstLetter}${secondLetter}${enrollmentItem?.enrollment_id || ""}`;
};

// Turn an enrollment record into the data the certificate needs.
// Anything missing falls back to the default certificate values.
const buildCertificateData = (item) => {
  const programName =
    item.cohort?.name || item.course?.title || "Training Program";

  const date = new Date(
    item.approved_at || item.completed_at || item.cohort?.end_date || Date.now(),
  ).toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return {
    name: item.user?.name || DEFAULT_CERTIFICATE.name,
    program: programName,
    date,
    description: item.description || DEFAULT_CERTIFICATE.description,
    directorName: item.director_name || DEFAULT_CERTIFICATE.directorName,
    directorTitle: item.director_title || DEFAULT_CERTIFICATE.directorTitle,
    instructorName: item.instructor_name || DEFAULT_CERTIFICATE.instructorName,
    instructorTitle:
      item.instructor_title || DEFAULT_CERTIFICATE.instructorTitle,
    certificateId:
      item.certificate_id || generateCertificateId(programName, item),
  };
};

/* ------------------------------------------------------------------ */
/* Corner chevrons – drawn in the template's 1280x904 coordinate space */
/* ------------------------------------------------------------------ */
const CertificateCorners = () => (
  <svg
    className="certificate-corners"
    viewBox="0 0 1280 904"
    preserveAspectRatio="none"
    aria-hidden="true"
  >
    {/* top-left */}
    <polyline points="95,-20 200,135 -20,350" />
    <polyline points="15,-20 105,130 -20,250" />
    {/* top-right */}
    <polyline points="1185,-20 1080,135 1300,350" />
    <polyline points="1265,-20 1175,130 1300,250" />
  </svg>
);

/* ------------------------------------------------------------------ */
/* The certificate itself (always rendered at the fixed A4 size)       */
/* ------------------------------------------------------------------ */
const CertificateView = forwardRef(({ data }, ref) => (
  <div ref={ref} className="certificate bg-image">
    <CertificateSVGCorners />

    <div className="certificate-header">
      <div className="certificate-logo">
        <img src={Logo} alt="GritinAI" />
      </div>
    </div>

    <h1 className="certificate-title">Certificate</h1>
    <p className="certificate-subtitle">OF COMPLETION</p>
    <p className="certificate-small-text">This certificate is awarded to :</p>

    <h2 className="certificate-recipient">{data.name}</h2>

    <p className="certificate-body">
      For the successful completion of the comprehensive{" "}
      <span className="certificate-program">{data.program}</span> on this day
      of {data.date}. {data.description}
    </p>

    <div className="certificate-signatures">
      <div className="certificate-signature-block">
        {DirectorSignature && (
          <img
            className="signature-image"
            src={DirectorSignature}
            alt="Director signature"
          />
        )}
        <div className="signature-line" />
        <div className="signature-name">{data.directorName}</div>
        <div className="signature-title">{data.directorTitle}</div>
      </div>

      <div className="certificate-signature-block">
        {InstructorSignature && (
          <img
            className="signature-image"
            src={InstructorSignature}
            alt="Instructor signature"
          />
        )}
        <div className="signature-line" />
        <div className="signature-name">{data.instructorName}</div>
        <div className="signature-title">{data.instructorTitle}</div>
      </div>
    </div>

    <div className="certificate-id">{data.certificateId}</div>
  </div>
));
CertificateView.displayName = "CertificateView";

// small alias so the JSX above reads cleanly
const CertificateSVGCorners = CertificateCorners;

/* ------------------------------------------------------------------ */
/* Scales the fixed-size certificate down to fit the screen            */
/* ------------------------------------------------------------------ */
const PreviewScaler = ({ children }) => {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const update = () => {
      if (wrapRef.current) {
        setScale(Math.min(1, wrapRef.current.clientWidth / CERT_W));
      }
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(wrapRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={wrapRef}
      className="certificate-preview"
      style={{ height: CERT_H * scale }}
    >
      <div
        style={{
          width: CERT_W,
          height: CERT_H,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }}
      >
        {children}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */
const Certificate = () => {
  const dispatch = useDispatch();
  const { enrollment, loading } = useSelector(
    (state) => state.userEnrolledCohorts,
  );
  const [selectedCohortId, setSelectedCohortId] = useState("");
  const [certificateData, setCertificateData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const { toPDF, targetRef } = usePDF({
    filename: "certificate.pdf",
    // IMPORTANT: `resolution` is used both as the html2canvas scale AND as the
    // divisor when react-to-pdf sizes the image on the page. They MUST be the
    // same value, otherwise the captured image is mis-sized and leaves white
    // space around it. Do not set a separate `overrides.canvas.scale`.
    resolution: 2,
    page: { format: "A4", orientation: "landscape", margin: 0 },
    canvas: { useCORS: true, logging: false },
  });

  useEffect(() => {
    dispatch(fetchUserEnrolledCohorts());
  }, [dispatch]);

  const handleCohortChange = (e) => {
    setSelectedCohortId(e.target.value);
    setCertificateData(null);
    setErrorMessage("");
  };

  const handleGenerateCertificate = () => {
    setErrorMessage("");

    if (!selectedCohortId) {
      setErrorMessage("Please select a cohort first.");
      return;
    }

    const enrollmentItem = enrollment.find(
      (item) =>
        String(item.cohort?.id) === String(selectedCohortId) ||
        String(item.id) === String(selectedCohortId),
    );

    if (!enrollmentItem) {
      setErrorMessage("Selected cohort not found.");
      return;
    }

    if (!enrollmentItem.approve_certificate) {
      setErrorMessage(
        "Your certificate has not been approved yet. Please contact the program administrator.",
      );
      return;
    }

    setCertificateData(buildCertificateData(enrollmentItem));
  };



  const handleDownloadCertificate = async () => {
    // make sure custom fonts are loaded before capturing
    if (document.fonts?.ready) await document.fonts.ready;
    toPDF();
  };

  const primaryBtn = (enabled) => ({
    padding: "12px 32px",
    fontSize: "15px",
    fontWeight: "600",
    backgroundColor: enabled ? "#0066cc" : "#ccc",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: enabled ? "pointer" : "not-allowed",
    transition: "all 0.3s ease",
    minWidth: "180px",
    height: "48px",
  });

  const canGenerate = selectedCohortId && !loading;

  return (
    <div
      style={{
        backgroundColor: "#f5f7fb",
        minHeight: "100vh",
        paddingInline: "3rem",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        {/* Page Header */}
        <div>
          <div>
            <h1
              style={{
                fontSize: "48px",
                fontWeight: "700",
                color: "#1a1a1a",
                marginBottom: "10px",
              }}
            >
              My Certificate
            </h1>
            <p
              style={{
                fontSize: "16px",
                color: "#666",
                marginBottom: "30px",
              }}
            >
              Your Certificate is a personalized digital credential recognizing
              your achievements and skills. It serves as official proof of
              completion for courses, training, or milestones, enhancing your
              professional profile.
            </p>
          </div>
          <div>
            <CertificateSVG />
          </div>
        </div>

        {/* Filter Section */}
        <div
          style={{
            backgroundColor: "#fff",
            padding: "30px",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            marginBottom: "30px",
            zIndex: 10,
            position: "relative",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "20px",
              alignItems: "flex-end",
              flexWrap: "wrap",
            }}
          >
            <div style={{ flex: "1", minWidth: "250px" }}>
              <label
                htmlFor="cohort-select"
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "600",
                  fontSize: "14px",
                  color: "#333",
                }}
              >
                Cohort:
              </label>
              <select
                id="cohort-select"
                value={selectedCohortId}
                onChange={handleCohortChange}
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  fontSize: "15px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  color: "#333",
                  cursor: "pointer",
                  outline: "none",
                  appearance: "none",
                  backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 12px center",
                  backgroundSize: "20px",
                  paddingRight: "40px",
                }}
              >
                <option value="">All Cohorts</option>
                {enrollment
                  ?.filter(
                    (item) =>
                      item.cohort !== null ||
                      item.class_option === "Private Class",
                  )
                  .map((item) => (
                    <option key={item.id} value={item.cohort?.id ?? item.id}>
                      {item.cohort?.name ||
                        `${item.course?.title || "Course"} (Private Class)`}
                    </option>
                  ))}
              </select>
            </div>

            <button
              onClick={handleGenerateCertificate}
              disabled={!canGenerate}
              style={primaryBtn(canGenerate)}
              onMouseEnter={(e) => {
                if (canGenerate) e.target.style.backgroundColor = "#0052a3";
              }}
              onMouseLeave={(e) => {
                if (canGenerate) e.target.style.backgroundColor = "#0066cc";
              }}
            >
              {loading ? "Loading..." : "Generate Certificate"}
            </button>

          </div>

          {errorMessage && (
            <div
              style={{
                padding: "12px 16px",
                marginTop: "20px",
                backgroundColor: "#fff3cd",
                color: "#856404",
                borderRadius: "8px",
                border: "1px solid #ffeaa7",
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <span style={{ fontSize: "18px" }}>⚠️</span>
              {errorMessage}
            </div>
          )}
        </div>

        {/* Certificate Display */}
        {certificateData && (
          <>
            <div className="certificate-actions">
              <button
                onClick={handleDownloadCertificate}
                style={{
                  padding: "12px 32px",
                  fontSize: "15px",
                  fontWeight: "600",
                  backgroundColor: "#28a745",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.backgroundColor = "#218838")
                }
                onMouseLeave={(e) =>
                  (e.target.style.backgroundColor = "#28a745")
                }
              >
                <span>📥</span> Download as PDF
              </button>
            </div>

            {/* Scaled preview (what the user sees) */}
            <PreviewScaler>
              <CertificateView data={certificateData} />
            </PreviewScaler>

            {/* Full-size copy used only for the PDF capture */}
            <div
              style={{
                position: "absolute",
                width: 0,
                height: 0,
                overflow: "hidden",
              }}
            >
              <CertificateView ref={targetRef} data={certificateData} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Certificate;